import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ArrowLeft, Plus, UserCheck, UserX, Trash2, Key, Copy, Edit, X, FileSpreadsheet, FileDown, Search, CreditCard, AlertTriangle, TrendingUp, Shield, Users } from 'lucide-react';
import { toast } from 'sonner';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

const API = process.env.REACT_APP_BACKEND_URL || '';

// Subscription plan display names
const PLAN_NAMES = {
  trial: 'Free Trial',
  hobby: 'Hobby',
  club: 'Club',
  association: 'Association',
  large_club: 'Large Club',
  free: '🎁 Free (Special)'
};

const PLAN_COLORS = {
  trial: 'bg-blue-100 text-blue-800',
  hobby: 'bg-amber-100 text-amber-800',
  club: 'bg-green-100 text-green-800',
  association: 'bg-purple-100 text-purple-800',
  large_club: 'bg-emerald-100 text-emerald-800',
  free: 'bg-emerald-100 text-emerald-800'
};

const STATUS_COLORS = {
  trial: 'bg-blue-100 text-blue-700',
  active: 'bg-green-100 text-green-700',
  expired: 'bg-red-100 text-red-700',
  cancelled: 'bg-stone-100 text-stone-700',
  suspended: 'bg-orange-100 text-orange-700'
};

const UserManagement = () => {
  const { token, isAdmin, user } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [subscriptionOverview, setSubscriptionOverview] = useState([]);
  const [subscriptionPlans, setSubscriptionPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('users'); // 'users', 'subscriptions', or 'activity'
  const [showAddUser, setShowAddUser] = useState(false);
  const [showEditUser, setShowEditUser] = useState(null);
  const [showCredentials, setShowCredentials] = useState(null);
  const [showResetPassword, setShowResetPassword] = useState(null);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showResetDataDialog, setShowResetDataDialog] = useState(false);
  const [resetKeepUsers, setResetKeepUsers] = useState({});
  const [resetConfirmText, setResetConfirmText] = useState('');
  const [resetting, setResetting] = useState(false);
  const [loginActivity, setLoginActivity] = useState([]);
  const [activityLoading, setActivityLoading] = useState(false);
  const [newUser, setNewUser] = useState({ 
    email: '', 
    password: '', 
    name: '', 
    company_name: '', 
    role: 'user',
    address: '',
    city: '',
    state: '',
    postal_code: '',
    country: '',
    phone: ''
  });
  const [editUser, setEditUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');

  useEffect(() => {
    if (!isAdmin) {
      navigate('/');
      return;
    }
    fetchUsers();
    fetchSubscriptionOverview();
    fetchSubscriptionPlans();
  }, [isAdmin, navigate, token]);

  const fetchSubscriptionPlans = async () => {
    if (!token) return;
    try {
      const response = await fetch(`${API}/api/admin/subscription-plans`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSubscriptionPlans(data);
      }
    } catch (err) {
    }
  };

  const fetchUsers = async () => {
    if (!token) return;
    try {
      const response = await fetch(`${API}/api/admin/users`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setUsers(data);
      } else {
      }
    } catch (err) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const fetchSubscriptionOverview = async () => {
    if (!token) return;
    try {
      const response = await fetch(`${API}/api/admin/users/subscriptions`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSubscriptionOverview(data);
      }
    } catch (err) {
    }
  };

  const fetchLoginActivity = async () => {
    if (!token) return;
    setActivityLoading(true);
    try {
      const response = await fetch(`${API}/api/admin/login-activity?limit=100`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setLoginActivity(data);
      }
    } catch (err) {
    } finally {
      setActivityLoading(false);
    }
  };


  // Helper to get plan name from dynamic data
  const getPlanName = (planId) => {
    const plan = subscriptionPlans.find(p => p.plan_id === planId);
    if (plan) return plan.name;
    return PLAN_NAMES[planId] || planId;
  };

  // Helper to get plan max hives from dynamic data
  const getPlanMaxHives = (planId) => {
    const plan = subscriptionPlans.find(p => p.plan_id === planId);
    if (plan) return plan.max_hives;
    return null;
  };

  const handleUpdateSubscription = async (userId, plan, status, billingCycle) => {
    try {
      const response = await fetch(
        `${API}/api/admin/users/${userId}/subscription?plan=${plan}&status=${status}&billing_cycle=${billingCycle}`,
        {
          method: 'PUT',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      if (response.ok) {
        toast.success('Subscription updated successfully!');
        setShowSubscriptionModal(null);
        fetchSubscriptionOverview();
        fetchUsers();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update subscription');
      }
    } catch (err) {
      toast.error('Failed to update subscription');
    }
  };

  const handleSuspendSubscription = async (userId) => {
    if (!window.confirm('Suspend this user\'s subscription? They will see a warning when adding hives.')) return;
    
    try {
      const response = await fetch(`${API}/api/admin/users/${userId}/subscription/suspend`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success('Subscription suspended');
        fetchSubscriptionOverview();
      } else {
        toast.error('Failed to suspend subscription');
      }
    } catch (err) {
      toast.error('Failed to suspend subscription');
    }
  };

  const handleReactivateSubscription = async (userId) => {
    try {
      const response = await fetch(`${API}/api/admin/users/${userId}/subscription/reactivate`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success('Subscription reactivated');
        fetchSubscriptionOverview();
      } else {
        toast.error('Failed to reactivate subscription');
      }
    } catch (err) {
      toast.error('Failed to reactivate subscription');
    }
  };

  const resetNewUser = () => {
    setNewUser({ 
      email: '', 
      password: '', 
      name: '', 
      company_name: '', 
      role: 'user',
      address: '',
      city: '',
      state: '',
      postal_code: '',
      country: '',
      phone: ''
    });
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    if (!newUser.email || !newUser.password) {
      toast.error('Email and password are required');
      return;
    }
    
    try {
      const response = await fetch(`${API}/api/admin/users`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newUser)
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success('User created successfully!');
        setShowCredentials(data.credentials);
        setShowAddUser(false);
        resetNewUser();
        fetchUsers();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create user');
      }
    } catch (err) {
      toast.error('Failed to create user');
    }
  };

  const handleEditUserOpen = (u) => {
    setEditUser({
      id: u.id,
      name: u.name || '',
      company_name: u.company_name || '',
      address: u.address || '',
      city: u.city || '',
      state: u.state || '',
      postal_code: u.postal_code || '',
      country: u.country || '',
      phone: u.phone || ''
    });
    setShowEditUser(u);
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${API}/api/admin/users/${editUser.id}`, {
        method: 'PUT',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(editUser)
      });
      
      if (response.ok) {
        toast.success('User updated successfully!');
        setShowEditUser(null);
        setEditUser(null);
        fetchUsers();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update user');
      }
    } catch (err) {
      toast.error('Failed to update user');
    }
  };

  const handleToggleStatus = async (userId, currentStatus) => {
    const action = currentStatus === 'active' ? 'deactivate' : 'activate';
    try {
      const response = await fetch(`${API}/api/admin/users/${userId}/${action}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        toast.success(`User ${action}d successfully`);
        fetchUsers();
      }
    } catch (err) {
      toast.error(`Failed to ${action} user`);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user? This cannot be undone.')) return;
    
    try {
      const response = await fetch(`${API}/api/admin/users/${userId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        toast.success('User deleted successfully');
        fetchUsers();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to delete user');
      }
    } catch (err) {
      toast.error('Failed to delete user');
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!newPassword) {
      toast.error('Please enter a new password');
      return;
    }
    
    try {
      const response = await fetch(`${API}/api/admin/users/${showResetPassword.id}/reset-password`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ password: newPassword })
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success('Password reset successfully!');
        setShowCredentials(data.credentials);
        setShowResetPassword(null);
        setNewPassword('');
      }
    } catch (err) {
      toast.error('Failed to reset password');
    }
  };

  const openResetDataDialog = () => {
    const keepMap = {};
    users.forEach(u => { keepMap[u.id] = u.role === 'admin'; });
    setResetKeepUsers(keepMap);
    setResetConfirmText('');
    setShowResetDataDialog(true);
  };

  const handleResetData = async () => {
    if (resetConfirmText !== 'RESET') return;
    setResetting(true);
    try {
      const keepIds = Object.entries(resetKeepUsers).filter(([, keep]) => keep).map(([id]) => id);
      const adminKept = users.find(u => u.role === 'admin' && keepIds.includes(u.id));
      if (!adminKept) {
        toast.error('You must keep at least the admin account');
        setResetting(false);
        return;
      }
      const res = await fetch(`${API}/api/admin/reset-data`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ confirm: 'RESET', keep_user_ids: keepIds })
      });
      const data = await res.json();
      if (res.ok) {
        toast.success('All activity data has been reset');
        setShowResetDataDialog(false);
        fetchUsers();
        fetchSubscriptionOverview();
      } else {
        toast.error(data.detail || 'Reset failed');
      }
    } catch (err) {
      toast.error('Reset failed');
    }
    setResetting(false);
  };

  const copyToClipboard = (text) => {
    // Use the fallback method which works more reliably
    try {
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.top = '0';
      textArea.style.left = '0';
      textArea.style.width = '2em';
      textArea.style.height = '2em';
      textArea.style.padding = '0';
      textArea.style.border = 'none';
      textArea.style.outline = 'none';
      textArea.style.boxShadow = 'none';
      textArea.style.background = 'transparent';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      
      const successful = document.execCommand('copy');
      document.body.removeChild(textArea);
      
      if (successful) {
        toast.success('Copied to clipboard!');
      } else {
        toast.error('Copy failed. Please select and copy manually.');
      }
    } catch (err) {
      toast.error('Copy failed. Please select and copy manually.');
    }
  };

  const exportToCSV = () => {
    const headers = ['Name', 'Email', 'Company', 'Phone', 'Address', 'City', 'State', 'Postal Code', 'Country', 'Role', 'Status', 'Signed Up', 'Last Login', 'T&C Accepted', 'T&C Version', 'T&C Date', 'Swarm Rescue', 'Mentor', 'Community Forum', 'Show on Map'];
    const rows = users.map(u => [
      u.name || '',
      u.email || '',
      u.company_name || '',
      u.phone || '',
      u.address || '',
      u.city || '',
      u.state || '',
      u.postal_code || '',
      u.country || '',
      u.role || '',
      u.status || '',
      u.created_at ? new Date(u.created_at).toLocaleString() : '',
      u.last_login ? new Date(u.last_login).toLocaleString() : '',
      u.terms_accepted ? 'Yes' : 'No',
      u.terms_version || '',
      u.terms_accepted_at ? new Date(u.terms_accepted_at).toLocaleString() : '',
      u.offers_swarm_rescue ? 'Yes' : 'No',
      u.offers_mentoring ? 'Yes' : 'No',
      u.offers_community_forum ? 'Yes' : 'No',
      u.show_on_map ? 'Yes' : 'No'
    ]);
    
    const csvContent = [headers, ...rows].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `users-export-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    toast.success('CSV exported successfully');
  };

  const exportToPDF = () => {
    const doc = new jsPDF('landscape');
    
    // Title
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text('User List Export', 14, 20);
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(`Generated: ${new Date().toLocaleString()}`, 14, 28);
    doc.text(`Total Users: ${users.length}`, 14, 34);
    
    // Table data
    const tableData = users.map(u => [
      u.name || '-',
      u.email || '-',
      u.company_name || '-',
      [u.city, u.state].filter(Boolean).join(', ') || '-',
      u.phone || '-',
      u.status || '-',
      u.created_at ? new Date(u.created_at).toLocaleDateString() : '-',
      u.last_login ? new Date(u.last_login).toLocaleDateString() : '-',
      u.terms_accepted ? `v${u.terms_version || '?'}` : 'No',
      u.terms_accepted_at ? new Date(u.terms_accepted_at).toLocaleDateString() : '-',
      [
        u.offers_swarm_rescue ? 'Swarm' : '',
        u.offers_mentoring ? 'Mentor' : '',
        u.offers_community_forum ? 'Forum' : ''
      ].filter(Boolean).join(', ') || '-'
    ]);
    
    autoTable(doc, {
      startY: 42,
      head: [['Name', 'Email', 'Company', 'Location', 'Phone', 'Status', 'Signed Up', 'Last Login', 'T&C', 'T&C Date', 'Services']],
      body: tableData,
      headStyles: { fillColor: [245, 158, 11], textColor: [255, 255, 255] },
      styles: { fontSize: 7, cellPadding: 2 },
      columnStyles: {
        0: { cellWidth: 22 },
        1: { cellWidth: 38 },
        2: { cellWidth: 22 },
        3: { cellWidth: 22 },
        4: { cellWidth: 22 },
        5: { cellWidth: 14 },
        6: { cellWidth: 20 },
        7: { cellWidth: 20 },
        8: { cellWidth: 14 },
        9: { cellWidth: 20 },
        10: { cellWidth: 28 }
      }
    });
    
    doc.save(`users-export-${new Date().toISOString().split('T')[0]}.pdf`);
    toast.success('PDF exported successfully');
  };

  const generateWelcomeLetter = () => {
    if (!showCredentials) return '';
    return `Welcome to Thrive!

Your account has been created. Here are your login details:

Email: ${showCredentials.email}
Password: ${showCredentials.password}

Login at: ${window.location.origin}

Please change your password after your first login.

Best regards,
${user?.name || 'Administrator'}`;
  };

  if (loading) {
    return <div className="flex items-center justify-center">Loading...</div>;
  }

  return (
    <div className="honeycomb-bg p-4">
      <div className="max-w-7xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-stone-800 flex items-center gap-2">
              <Users className="h-6 w-6 text-amber-600" />
              User Management
            </h1>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button variant="outline" onClick={exportToCSV} className="border-lime-300 text-lime-700 hover:bg-lime-50">
              <FileSpreadsheet className="w-4 h-4 mr-2" /> CSV
            </Button>
            <Button variant="outline" onClick={exportToPDF} className="border-rose-300 text-rose-700 hover:bg-rose-50">
              <FileDown className="w-4 h-4 mr-2" /> PDF
            </Button>
            <Button onClick={() => setShowAddUser(true)} className="bg-amber-600 hover:bg-amber-700">
              <Plus className="w-4 h-4 mr-2" /> Add User
            </Button>
            <Button
              data-testid="data-reset-btn"
              variant="outline"
              onClick={openResetDataDialog}
              className="border-red-300 text-red-700 hover:bg-red-50"
            >
              <Trash2 className="w-4 h-4 mr-2" /> Data Reset
            </Button>
          </div>
        </div>

        {/* Search Bar */}
        <div className="mb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-stone-400" />
            <Input
              type="text"
              placeholder="Search users by name, email, or company..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 h-12 bg-white border-stone-200 rounded-xl"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 p-1 hover:bg-stone-100 rounded-full"
              >
                <X className="w-4 h-4 text-stone-400" />
              </button>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-4">
          <button
            onClick={() => setActiveTab('users')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              activeTab === 'users'
                ? 'bg-amber-600 text-white'
                : 'bg-white text-stone-600 hover:bg-stone-100 border border-stone-200'
            }`}
          >
            👥 Users ({users.length})
          </button>
          <button
            onClick={() => setActiveTab('subscriptions')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
              activeTab === 'subscriptions'
                ? 'bg-green-600 text-white'
                : 'bg-white text-stone-600 hover:bg-stone-100 border border-stone-200'
            }`}
          >
            <CreditCard className="w-4 h-4" />
            Subscriptions
            {subscriptionOverview.filter(u => u.is_over_quota || u.subscription_status === 'expired').length > 0 && (
              <span className="bg-red-500 text-white text-xs px-1.5 py-0.5 rounded-full">
                {subscriptionOverview.filter(u => u.is_over_quota || u.subscription_status === 'expired').length}
              </span>
            )}
          </button>
          <button
            onClick={() => { setActiveTab('activity'); if (loginActivity.length === 0) fetchLoginActivity(); }}
            className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
              activeTab === 'activity'
                ? 'bg-stone-700 text-white'
                : 'bg-white text-stone-600 hover:bg-stone-100 border border-stone-200'
            }`}
            data-testid="login-activity-tab"
          >
            <Shield className="w-4 h-4" />
            Login Activity
          </button>
        </div>

        {/* Login Activity Tab */}
        {activeTab === 'activity' && (
          <div className="bg-white rounded-2xl shadow-lg border border-stone-200 overflow-hidden mb-6">
            <div className="p-4 bg-stone-50 border-b border-stone-200 flex items-center justify-between">
              <div>
                <h3 className="font-bold text-stone-800">Login Activity</h3>
                <p className="text-sm text-stone-500">Recent login attempts across all users</p>
              </div>
              <Button variant="outline" size="sm" onClick={fetchLoginActivity} disabled={activityLoading}>
                {activityLoading ? 'Loading...' : 'Refresh'}
              </Button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="login-activity-table">
                <thead>
                  <tr className="bg-stone-50 border-b border-stone-200">
                    <th className="text-left p-3 font-semibold text-stone-600">Time</th>
                    <th className="text-left p-3 font-semibold text-stone-600">Email</th>
                    <th className="text-left p-3 font-semibold text-stone-600">Name</th>
                    <th className="text-left p-3 font-semibold text-stone-600">Type</th>
                    <th className="text-left p-3 font-semibold text-stone-600">Status</th>
                    <th className="text-left p-3 font-semibold text-stone-600">IP Address</th>
                    <th className="text-left p-3 font-semibold text-stone-600">Device</th>
                  </tr>
                </thead>
                <tbody>
                  {loginActivity.length === 0 && !activityLoading && (
                    <tr><td colSpan={7} className="p-8 text-center text-stone-400">No login activity recorded yet. Activity will appear here after the next login.</td></tr>
                  )}
                  {loginActivity.map((a, idx) => (
                    <tr key={a.id || idx} className={`border-b border-stone-100 hover:bg-stone-50 ${a.status === 'failed' ? 'bg-red-50/50' : ''}`}>
                      <td className="p-3 text-stone-600 whitespace-nowrap">
                        {a.timestamp ? new Date(a.timestamp).toLocaleString('en-AU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—'}
                      </td>
                      <td className="p-3 font-mono text-xs text-stone-700">{a.email}</td>
                      <td className="p-3 text-stone-700">{a.user_name || '—'}</td>
                      <td className="p-3">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                          a.user_type === 'admin' ? 'bg-purple-100 text-purple-700' :
                          a.user_type === 'crew' ? 'bg-blue-100 text-blue-700' :
                          'bg-stone-100 text-stone-600'
                        }`}>
                          {a.user_type || 'user'}
                        </span>
                      </td>
                      <td className="p-3">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-bold ${
                          a.status === 'success' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                        }`}>
                          {a.status === 'success' ? 'Success' : 'Failed'}
                        </span>
                        {a.reason && <span className="text-xs text-red-500 ml-1">({a.reason})</span>}
                      </td>
                      <td className="p-3 font-mono text-xs text-stone-500">
                        {a.ip_address}
                        {a.new_ip && <span className="ml-1.5 text-[10px] bg-red-100 text-red-700 font-bold px-1.5 py-0.5 rounded-full">NEW</span>}
                      </td>
                      <td className="p-3 text-stone-500 text-xs">{a.browser} / {a.device}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Subscriptions Tab */}
        {activeTab === 'subscriptions' && (
          <div className="bg-white rounded-2xl shadow-lg border border-stone-200 overflow-hidden mb-6">
            <div className="p-4 border-b border-stone-200 bg-gradient-to-r from-green-50 to-emerald-50">
              <h2 className="font-semibold text-stone-800 flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-green-600" />
                Subscription Overview
              </h2>
              <div className="flex items-center gap-2">
                <p className="text-sm text-stone-500">Manage user plans, view quotas, and activate subscriptions after EFT payment</p>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => navigate('/admin/subscription-plans')}
                  className="ml-auto text-green-600 border-green-200 hover:bg-green-50"
                >
                  ⚙️ Edit Plans
                </Button>
              </div>
            </div>
            
            {/* Stats Summary */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-stone-50 border-b border-stone-200">
              <div className="text-center">
                <p className="text-2xl font-bold text-blue-600">{subscriptionOverview.filter(u => u.subscription_status === 'trial').length}</p>
                <p className="text-xs text-stone-500">On Trial</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-green-600">{subscriptionOverview.filter(u => u.subscription_status === 'active').length}</p>
                <p className="text-xs text-stone-500">Active</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-orange-600">{subscriptionOverview.filter(u => u.quota_percent >= 80 && u.quota_percent < 100).length}</p>
                <p className="text-xs text-stone-500">Near Quota</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-red-600">{subscriptionOverview.filter(u => u.is_over_quota).length}</p>
                <p className="text-xs text-stone-500">Over Quota</p>
              </div>
            </div>
            
            <div className="divide-y divide-stone-100">
              {subscriptionOverview.length === 0 ? (
                <div className="p-8 text-center text-stone-500">No users found</div>
              ) : (
                subscriptionOverview
                  .filter(u => !searchQuery || 
                    u.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                    u.email?.toLowerCase().includes(searchQuery.toLowerCase())
                  )
                  .map(sub => (
                  <div key={sub.id} className="p-4 hover:bg-stone-50">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium text-stone-800">{sub.name || sub.email}</span>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${PLAN_COLORS[sub.subscription_plan] || 'bg-stone-100'}`}>
                            {getPlanName(sub.subscription_plan)}
                          </span>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[sub.subscription_status] || 'bg-stone-100'}`}>
                            {sub.subscription_status}
                          </span>
                          {sub.is_over_quota && (
                            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700 flex items-center gap-1">
                              <AlertTriangle className="w-3 h-3" /> Over Quota
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-stone-500">{sub.email}</p>
                        {sub.company_name && <p className="text-xs text-stone-400">{sub.company_name}</p>}
                      </div>
                      
                      {/* Quota Bar */}
                      <div className="w-full md:w-48">
                        {sub.is_admin ? (
                          <div className="text-xs text-emerald-600 font-medium">
                            {sub.hive_count} hives — Unlimited (Admin)
                          </div>
                        ) : (
                          <>
                            <div className="flex justify-between text-xs text-stone-500 mb-1">
                              <span>{sub.hive_count} / {sub.max_hives} hives</span>
                              <span>{sub.quota_percent}%</span>
                            </div>
                            <div className="h-2 bg-stone-200 rounded-full overflow-hidden">
                              <div 
                                className={`h-full transition-all ${
                                  sub.quota_percent >= 100 ? 'bg-red-500' :
                                  sub.quota_percent >= 80 ? 'bg-orange-500' :
                                  'bg-green-500'
                                }`}
                                style={{ width: `${Math.min(sub.quota_percent, 100)}%` }}
                              />
                            </div>
                          </>
                        )}
                      </div>
                      
                      {/* Actions */}
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setShowSubscriptionModal(sub)}
                          className="text-green-600 border-green-200 hover:bg-green-50"
                        >
                          <TrendingUp className="w-4 h-4 mr-1" />
                          Manage
                        </Button>
                        {sub.subscription_status === 'active' && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleSuspendSubscription(sub.id)}
                            className="text-orange-600 border-orange-200 hover:bg-orange-50"
                          >
                            Suspend
                          </Button>
                        )}
                        {sub.subscription_status === 'suspended' && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleReactivateSubscription(sub.id)}
                            className="text-green-600 border-green-200 hover:bg-green-50"
                          >
                            Reactivate
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* Users List */}
        {activeTab === 'users' && (
        <div className="bg-white rounded-2xl shadow-lg border border-stone-200 overflow-hidden">
          <div className="p-4 border-b border-stone-200 bg-stone-50">
            <h2 className="font-semibold text-stone-800">
              {searchQuery ? `Search Results (${users.filter(u => 
                (u.name?.toLowerCase() || '').includes(searchQuery.toLowerCase()) ||
                (u.email?.toLowerCase() || '').includes(searchQuery.toLowerCase()) ||
                (u.company_name?.toLowerCase() || '').includes(searchQuery.toLowerCase())
              ).length})` : `All Users (${users.length})`}
            </h2>
          </div>
          <div className="divide-y divide-stone-100">
            {users.filter(u => 
              !searchQuery || 
              (u.name?.toLowerCase() || '').includes(searchQuery.toLowerCase()) ||
              (u.email?.toLowerCase() || '').includes(searchQuery.toLowerCase()) ||
              (u.company_name?.toLowerCase() || '').includes(searchQuery.toLowerCase())
            ).length === 0 ? (
              <div className="p-8 text-center text-stone-500">
                {searchQuery ? 'No users match your search' : 'No users found'}
              </div>
            ) : (
              users.filter(u => 
                !searchQuery || 
                (u.name?.toLowerCase() || '').includes(searchQuery.toLowerCase()) ||
                (u.email?.toLowerCase() || '').includes(searchQuery.toLowerCase()) ||
                (u.company_name?.toLowerCase() || '').includes(searchQuery.toLowerCase())
              ).map(u => (
                <div key={u.id} className={`p-4 flex items-center justify-between ${u.status === 'inactive' ? 'bg-stone-50 opacity-60' : ''}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-stone-800">{u.name || 'No name'}</span>
                      {u.role === 'admin' && (
                        <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded-full">Admin</span>
                      )}
                      {u.is_bee_club && (
                        <span className="px-2 py-0.5 bg-violet-100 text-violet-700 text-xs rounded-full">Bee Club Rep</span>
                      )}
                      {u.status === 'inactive' && (
                        <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs rounded-full">Inactive</span>
                      )}
                      <button
                        onClick={() => setShowSubscriptionModal(u)}
                        className={`px-2 py-0.5 text-xs rounded-full font-medium cursor-pointer hover:opacity-80 transition ${PLAN_COLORS[u.subscription_plan] || 'bg-stone-100 text-stone-600 border border-dashed border-stone-300'}`}
                        title="Click to change plan"
                        data-testid={`plan-badge-${u.id}`}
                      >
                        {u.subscription_plan ? getPlanName(u.subscription_plan) : 'No Plan — Assign'}
                      </button>
                    </div>
                    <div className="text-sm text-stone-500">{u.email}</div>
                    {u.company_name && <div className="text-sm text-stone-400">{u.company_name}</div>}
                    {(u.city || u.state) && (
                      <div className="text-xs text-stone-400 mt-1">
                        📍 {[u.city, u.state, u.country].filter(Boolean).join(', ')}
                      </div>
                    )}
                    <div className="flex flex-wrap gap-x-4 gap-y-0.5 mt-1.5 text-xs text-stone-400">
                      {u.created_at && (
                        <span title="Signup date">📅 Signed up: {new Date(u.created_at).toLocaleDateString()} {new Date(u.created_at).toLocaleTimeString([], {hour:'2-digit', minute:'2-digit'})}</span>
                      )}
                      {u.last_login && (
                        <span title="Last login">🔑 Last login: {new Date(u.last_login).toLocaleDateString()} {new Date(u.last_login).toLocaleTimeString([], {hour:'2-digit', minute:'2-digit'})}</span>
                      )}
                      <span title="Terms & Conditions" className={u.terms_accepted ? 'text-green-600' : 'text-red-500'}>
                        {u.terms_accepted ? `📜 T&C v${u.terms_version || '?'}` : '📜 T&C not accepted'}
                        {u.terms_accepted_at && ` (${new Date(u.terms_accepted_at).toLocaleDateString()})`}
                      </span>
                    </div>
                  </div>
                  {u.role !== 'admin' && (
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleEditUserOpen(u)}
                        className="p-2 text-stone-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg"
                        title="Edit User"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setShowResetPassword(u)}
                        className="p-2 text-stone-500 hover:text-amber-600 hover:bg-amber-50 rounded-lg"
                        title="Reset Password"
                      >
                        <Key className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleToggleStatus(u.id, u.status)}
                        className={`p-2 rounded-lg ${u.status === 'active' ? 'text-stone-500 hover:text-red-600 hover:bg-red-50' : 'text-stone-500 hover:text-green-600 hover:bg-green-50'}`}
                        title={u.status === 'active' ? 'Deactivate' : 'Activate'}
                      >
                        {u.status === 'active' ? <UserX className="w-4 h-4" /> : <UserCheck className="w-4 h-4" />}
                      </button>
                      <button
                        onClick={() => handleDeleteUser(u.id)}
                        className="p-2 text-stone-500 hover:text-red-600 hover:bg-red-50 rounded-lg"
                        title="Delete User"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
        )}

        {/* Subscription Management Modal */}
        {showSubscriptionModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowSubscriptionModal(null)}>
            <div className="bg-white rounded-2xl p-6 max-w-md w-full mx-4" onClick={e => e.stopPropagation()}>
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold text-stone-800">Manage Subscription</h3>
                <button onClick={() => setShowSubscriptionModal(null)} className="p-1 hover:bg-stone-100 rounded">
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="mb-4 p-3 bg-stone-50 rounded-lg">
                <p className="font-medium text-stone-800">{showSubscriptionModal.name || showSubscriptionModal.email}</p>
                <p className="text-sm text-stone-500">{showSubscriptionModal.email}</p>
                <p className="text-xs text-stone-400 mt-1">
                  Current: {getPlanName(showSubscriptionModal.subscription_plan) || 'No Plan'}
                  {showSubscriptionModal.hive_count != null && ` • ${showSubscriptionModal.hive_count}/${getPlanMaxHives(showSubscriptionModal.subscription_plan) || showSubscriptionModal.max_hives || '?'} hives`}
                </p>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-2">Select Plan</label>
                  <div className="grid grid-cols-2 gap-2">
                    {subscriptionPlans
                      .filter(plan => plan.is_visible !== false && plan.plan_id !== 'free')
                      .sort((a, b) => (a.sort_order || a.price_monthly || 0) - (b.sort_order || b.price_monthly || 0))
                      .map(plan => (
                      <button
                        key={plan.plan_id}
                        onClick={() => handleUpdateSubscription(
                          showSubscriptionModal.id, 
                          plan.plan_id, 
                          'active', 
                          showSubscriptionModal.billing_cycle || 'monthly'
                        )}
                        className={`p-3 rounded-lg border-2 text-left transition-colors ${
                          showSubscriptionModal.subscription_plan === plan.plan_id
                            ? 'border-green-500 bg-green-50'
                            : 'border-stone-200 hover:border-green-300 hover:bg-green-50'
                        }`}
                      >
                        <p className="font-medium text-stone-800">{plan.name}</p>
                        <p className="text-xs text-stone-500">
                          {plan.hive_range || `Up to ${plan.max_hives} hives`}
                        </p>
                      </button>
                    ))}
                  </div>
                  
                  {/* Special Free Plan for Testers/Contributors */}
                  {subscriptionPlans.find(p => p.plan_id === 'free') && (
                  <div className="mt-4 pt-4 border-t border-stone-200">
                    <p className="text-xs font-medium text-emerald-700 mb-2">🎁 Special Gift Plan</p>
                    {subscriptionPlans
                      .filter(plan => plan.plan_id === 'free')
                      .map(plan => (
                    <button
                      key={plan.plan_id}
                      onClick={() => handleUpdateSubscription(
                        showSubscriptionModal.id, 
                        'free', 
                        'active', 
                        'lifetime'
                      )}
                      className={`w-full p-3 rounded-lg border-2 text-left transition-colors ${
                        showSubscriptionModal.subscription_plan === 'free'
                          ? 'border-emerald-500 bg-emerald-50'
                          : 'border-emerald-200 hover:border-emerald-400 hover:bg-emerald-50'
                      }`}
                    >
                      <p className="font-medium text-emerald-800">🎁 {plan.name}</p>
                      <p className="text-xs text-emerald-600">
                        {plan.hive_range || `Up to ${plan.max_hives} hives`} • No expiry
                      </p>
                    </button>
                    ))}
                  </div>
                  )}
                </div>
                
                <div className="pt-4 border-t border-stone-200">
                  <p className="text-xs text-stone-500 mb-2">
                    💡 Click a plan to activate it. Use this after receiving EFT payment.
                  </p>
                  <div className="flex gap-2">
                    {showSubscriptionModal.subscription_status !== 'suspended' ? (
                      <Button
                        variant="outline"
                        onClick={() => {
                          handleSuspendSubscription(showSubscriptionModal.id);
                          setShowSubscriptionModal(null);
                        }}
                        className="flex-1 text-orange-600 border-orange-200 hover:bg-orange-50"
                      >
                        Suspend (No Payment)
                      </Button>
                    ) : (
                      <Button
                        variant="outline"
                        onClick={() => {
                          handleReactivateSubscription(showSubscriptionModal.id);
                          setShowSubscriptionModal(null);
                        }}
                        className="flex-1 text-green-600 border-green-200 hover:bg-green-50"
                      >
                        Reactivate (Payment Received)
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Add User Modal */}
        {showAddUser && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowAddUser(false)}>
            <div className="bg-white rounded-2xl p-6 max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold text-stone-800">Add New User</h3>
                <button onClick={() => setShowAddUser(false)} className="p-1 hover:bg-stone-100 rounded">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <form onSubmit={handleCreateUser} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-stone-700 mb-1">Name</label>
                    <Input
                      value={newUser.name}
                      onChange={e => setNewUser({...newUser, name: e.target.value})}
                      placeholder="John Doe"
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-stone-700 mb-1">Company Name</label>
                    <Input
                      value={newUser.company_name}
                      onChange={e => setNewUser({...newUser, company_name: e.target.value})}
                      placeholder="Bee Farm LLC"
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-stone-700 mb-1">Email *</label>
                    <Input
                      type="email"
                      value={newUser.email}
                      onChange={e => setNewUser({...newUser, email: e.target.value})}
                      placeholder="user@email.com"
                      required
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-stone-700 mb-1">Password *</label>
                    <Input
                      type="text"
                      value={newUser.password}
                      onChange={e => setNewUser({...newUser, password: e.target.value})}
                      placeholder="Initial password"
                      required
                    />
                    <p className="text-xs text-stone-500 mt-1">User can change this after first login</p>
                  </div>
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-stone-700 mb-1">Phone</label>
                    <Input
                      value={newUser.phone}
                      onChange={e => setNewUser({...newUser, phone: e.target.value})}
                      placeholder="+1 555-123-4567"
                    />
                  </div>
                </div>

                {/* Address Section */}
                <div className="border-t border-stone-200 pt-4 mt-4">
                  <h4 className="text-sm font-medium text-stone-600 mb-3">📍 Address (Optional)</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2">
                      <label className="block text-xs text-stone-500 mb-1">Street Address</label>
                      <Input
                        value={newUser.address}
                        onChange={e => setNewUser({...newUser, address: e.target.value})}
                        placeholder="123 Main St"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-stone-500 mb-1">City</label>
                      <Input
                        value={newUser.city}
                        onChange={e => setNewUser({...newUser, city: e.target.value})}
                        placeholder="Melbourne"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-stone-500 mb-1">State/Province</label>
                      <Input
                        value={newUser.state}
                        onChange={e => setNewUser({...newUser, state: e.target.value})}
                        placeholder="VIC"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-stone-500 mb-1">Postal Code</label>
                      <Input
                        value={newUser.postal_code}
                        onChange={e => setNewUser({...newUser, postal_code: e.target.value})}
                        placeholder="3000"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-stone-500 mb-1">Country</label>
                      <Input
                        value={newUser.country}
                        onChange={e => setNewUser({...newUser, country: e.target.value})}
                        placeholder="Australia"
                      />
                    </div>
                  </div>
                </div>

                <div className="flex gap-3 pt-2">
                  <Button type="button" variant="outline" onClick={() => setShowAddUser(false)} className="flex-1">
                    Cancel
                  </Button>
                  <Button type="submit" className="flex-1 bg-amber-600 hover:bg-amber-700">
                    Create User
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Edit User Modal */}
        {showEditUser && editUser && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => { setShowEditUser(null); setEditUser(null); }}>
            <div className="bg-white rounded-2xl p-6 max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold text-stone-800">Edit User</h3>
                <button onClick={() => { setShowEditUser(null); setEditUser(null); }} className="p-1 hover:bg-stone-100 rounded">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <p className="text-sm text-stone-500 mb-4">{showEditUser.email}</p>
              <form onSubmit={handleUpdateUser} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-stone-700 mb-1">Name</label>
                    <Input
                      value={editUser.name}
                      onChange={e => setEditUser({...editUser, name: e.target.value})}
                      placeholder="John Doe"
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-stone-700 mb-1">Company Name</label>
                    <Input
                      value={editUser.company_name}
                      onChange={e => setEditUser({...editUser, company_name: e.target.value})}
                      placeholder="Bee Farm LLC"
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-stone-700 mb-1">Phone</label>
                    <Input
                      value={editUser.phone}
                      onChange={e => setEditUser({...editUser, phone: e.target.value})}
                      placeholder="+1 555-123-4567"
                    />
                  </div>
                </div>

                {/* Address Section */}
                <div className="border-t border-stone-200 pt-4 mt-4">
                  <h4 className="text-sm font-medium text-stone-600 mb-3">📍 Address (Optional)</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2">
                      <label className="block text-xs text-stone-500 mb-1">Street Address</label>
                      <Input
                        value={editUser.address}
                        onChange={e => setEditUser({...editUser, address: e.target.value})}
                        placeholder="123 Main St"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-stone-500 mb-1">City</label>
                      <Input
                        value={editUser.city}
                        onChange={e => setEditUser({...editUser, city: e.target.value})}
                        placeholder="Melbourne"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-stone-500 mb-1">State/Province</label>
                      <Input
                        value={editUser.state}
                        onChange={e => setEditUser({...editUser, state: e.target.value})}
                        placeholder="VIC"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-stone-500 mb-1">Postal Code</label>
                      <Input
                        value={editUser.postal_code}
                        onChange={e => setEditUser({...editUser, postal_code: e.target.value})}
                        placeholder="3000"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-stone-500 mb-1">Country</label>
                      <Input
                        value={editUser.country}
                        onChange={e => setEditUser({...editUser, country: e.target.value})}
                        placeholder="Australia"
                      />
                    </div>
                  </div>
                </div>

                <div className="flex gap-3 pt-2">
                  <Button type="button" variant="outline" onClick={() => { setShowEditUser(null); setEditUser(null); }} className="flex-1">
                    Cancel
                  </Button>
                  <Button type="submit" className="flex-1 bg-amber-600 hover:bg-amber-700">
                    Save Changes
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Reset Password Modal */}
        {showResetPassword && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowResetPassword(null)}>
            <div className="bg-white rounded-2xl p-6 max-w-md w-full mx-4" onClick={e => e.stopPropagation()}>
              <h3 className="text-xl font-bold text-stone-800 mb-4">Reset Password</h3>
              <p className="text-stone-600 mb-4">Set a new password for <strong>{showResetPassword.email}</strong></p>
              <form onSubmit={handleResetPassword} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">New Password</label>
                  <Input
                    type="text"
                    value={newPassword}
                    onChange={e => setNewPassword(e.target.value)}
                    placeholder="New password"
                    required
                  />
                </div>
                <div className="flex gap-3">
                  <Button type="button" variant="outline" onClick={() => setShowResetPassword(null)} className="flex-1">
                    Cancel
                  </Button>
                  <Button type="submit" className="flex-1 bg-amber-600 hover:bg-amber-700">
                    Reset Password
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Credentials Modal (Welcome Letter) */}
        {showCredentials && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowCredentials(null)}>
            <div className="bg-white rounded-2xl p-6 max-w-lg w-full mx-4" onClick={e => e.stopPropagation()}>
              <h3 className="text-xl font-bold text-stone-800 mb-2">✅ Credentials Ready</h3>
              <p className="text-stone-600 mb-4">Copy these credentials to send to the user:</p>
              
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm font-medium text-stone-600">Email:</span>
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-stone-800">{showCredentials.email}</span>
                    <button onClick={() => copyToClipboard(showCredentials.email)} className="p-1 hover:bg-amber-100 rounded">
                      <Copy className="w-4 h-4 text-amber-600" />
                    </button>
                  </div>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-stone-600">Password:</span>
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-stone-800">{showCredentials.password}</span>
                    <button onClick={() => copyToClipboard(showCredentials.password)} className="p-1 hover:bg-amber-100 rounded">
                      <Copy className="w-4 h-4 text-amber-600" />
                    </button>
                  </div>
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-stone-700 mb-2">📧 Welcome Letter Template:</label>
                <textarea
                  className="w-full h-40 p-3 border border-stone-200 rounded-xl text-sm font-mono"
                  value={generateWelcomeLetter()}
                  readOnly
                />
                <button
                  onClick={() => copyToClipboard(generateWelcomeLetter())}
                  className="mt-2 flex items-center gap-2 text-amber-600 hover:text-amber-700 text-sm"
                >
                  <Copy className="w-4 h-4" /> Copy Welcome Letter
                </button>
              </div>

              <Button onClick={() => setShowCredentials(null)} className="w-full">
                Done
              </Button>
            </div>
          </div>
        )}

        {/* Data Reset Modal */}
        {showResetDataDialog && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowResetDataDialog(false)}>
            <div className="bg-white rounded-2xl p-6 max-w-lg w-full mx-4 max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="data-reset-modal">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 bg-red-100 rounded-lg">
                  <AlertTriangle className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-stone-800">Data Reset</h3>
                  <p className="text-sm text-stone-500">This action cannot be undone</p>
                </div>
              </div>

              <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-4">
                <p className="text-sm text-red-800 font-medium mb-1">This will permanently delete:</p>
                <ul className="text-sm text-red-700 list-disc list-inside space-y-1">
                  <li>All apiaries, hives, and inspections</li>
                  <li>All harvests, products, and sales records</li>
                  <li>All community posts, events, and chat messages</li>
                  <li>All treatment plans and crew tasks</li>
                  <li>All uploaded files and swarm traps</li>
                  <li>Users not selected below will be removed</li>
                </ul>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-bold text-stone-700 mb-2">Keep these user accounts:</label>
                <div className="space-y-2 max-h-48 overflow-y-auto border border-stone-200 rounded-xl p-3">
                  {users.map(u => (
                    <label key={u.id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-stone-50 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={!!resetKeepUsers[u.id]}
                        onChange={(e) => setResetKeepUsers(prev => ({ ...prev, [u.id]: e.target.checked }))}
                        className="w-4 h-4 accent-amber-600"
                        data-testid={`keep-user-${u.id}`}
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-stone-800 truncate">{u.name || u.email}</p>
                        <p className="text-xs text-stone-500">{u.email} &middot; {u.role}</p>
                      </div>
                      {u.role === 'admin' && (
                        <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-medium">Admin</span>
                      )}
                    </label>
                  ))}
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-bold text-stone-700 mb-2">
                  Type <span className="font-mono text-red-600 bg-red-50 px-1.5 py-0.5 rounded">RESET</span> to confirm:
                </label>
                <Input
                  data-testid="reset-confirm-input"
                  value={resetConfirmText}
                  onChange={(e) => setResetConfirmText(e.target.value)}
                  placeholder="Type RESET"
                  className="border-stone-300"
                />
              </div>

              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => setShowResetDataDialog(false)}
                  className="flex-1"
                  data-testid="reset-cancel-btn"
                >
                  Cancel
                </Button>
                <Button
                  data-testid="reset-confirm-btn"
                  onClick={handleResetData}
                  disabled={resetConfirmText !== 'RESET' || resetting}
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white disabled:opacity-50"
                >
                  {resetting ? 'Resetting...' : 'Reset All Data'}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserManagement;
